import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-preact';
import { Portal } from '../overlays/Portal';
import { toasts, removeToast, type Toast, type ToastVariant } from '@/stores/toasts';
import { Icon } from '../primitives/Icon';
import { IconButton } from '../primitives/Button';
import { cn } from '@/lib/cn';

const ICON: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const COLOR: Record<ToastVariant, string> = {
  success: 'text-ok border-border-subtle before:bg-ok',
  error: 'text-error border-border-subtle before:bg-error',
  warning: 'text-warning border-border-subtle before:bg-warning',
  info: 'text-info border-border-subtle before:bg-info',
};

export function Toaster() {
  return (
    <Portal layer="toast">
      <div
        role="region"
        aria-label="Notificaciones"
        aria-live="polite"
        aria-relevant="additions"
        className="pointer-events-none fixed inset-0 flex flex-col items-end justify-end gap-2 p-4 sm:p-6"
      >
        <ul className="pointer-events-auto flex w-full max-w-sm flex-col gap-2">
          {toasts.value.map((t) => (
            <ToastItem key={t.id} toast={t} />
          ))}
        </ul>
      </div>
    </Portal>
  );
}

function ToastItem({ toast }: { toast: Toast }) {
  const variant = toast.variant;
  const dismissing = toast.dismissing === true;
  return (
    <li
      role={variant === 'error' ? 'alert' : 'status'}
      className={cn(
        'group relative flex items-start gap-3 overflow-hidden rounded-md border bg-surface px-3.5 py-3 shadow-md',
        'transition-[opacity,box-shadow,transform] duration-med hover:shadow-lg hover:-translate-y-px',
        'before:absolute before:inset-y-0 before:left-0 before:w-0.5',
        dismissing ? 'animate-slide-out-right' : 'animate-slide-in-right',
        COLOR[variant],
      )}
    >
      <Icon as={ICON[variant]} size={18} className="mt-0.5" />
      <div className="flex-1 text-13 text-fg-strong">
        {toast.title ? <p className="font-semibold">{toast.title}</p> : null}
        <p className="text-fg">{toast.message}</p>
        {toast.action ? (
          <button
            type="button"
            className="mt-1 text-xs font-medium text-brand hover:underline"
            onClick={() => {
              toast.action?.onClick();
              removeToast(toast.id);
            }}
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      <IconButton
        icon={X}
        label="Cerrar notificación"
        size="sm"
        variant="ghost"
        onClick={() => removeToast(toast.id)}
      />
    </li>
  );
}
